import {
  LogIn, ShoppingBag, Package, CheckSquare, Store, Key, Users, Settings,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ─── Shared constants for the Audit Log tab ────────────────────────────────────
// Single source of truth for activity-type colors/icons/labels and login-result colors, shared
// by AuditFilterBar, ActivityTimeline, LoginActivityTable, UserAvatar's type badge, and the KPI
// cards — so a color/label never drifts between two of those components.

export type ActivityType = 'auth' | 'orders' | 'inventory' | 'tasks' | 'vendors' | 'keys' | 'users' | 'settings';
export type LoginResult = 'success' | 'failed' | 'blocked' | 'locked';

export interface ActivityTypeMeta {
  label: string;
  color: string;
  Icon: LucideIcon;
}

export const ACTIVITY_TYPE_META: Record<ActivityType, ActivityTypeMeta> = {
  auth:      { label: 'Sign-in',           color: '#3b82f6', Icon: LogIn },
  orders:    { label: 'Orders',            color: '#f59e0b', Icon: ShoppingBag },
  inventory: { label: 'Inventory',         color: '#10b981', Icon: Package },
  tasks:     { label: 'Tasks & WOs',       color: '#8b5cf6', Icon: CheckSquare },
  vendors:   { label: 'Vendors',           color: '#06b6d4', Icon: Store },
  keys:      { label: 'Keys',              color: '#f97316', Icon: Key },
  users:     { label: 'Users & roles',     color: '#ec4899', Icon: Users },
  settings:  { label: 'Settings & billing', color: '#9ca3af', Icon: Settings },
};

export const ACTIVITY_TYPE_ORDER: ActivityType[] = ['auth', 'orders', 'inventory', 'tasks', 'vendors', 'keys', 'users', 'settings'];

export const RESULT_META: Record<LoginResult, { label: string; color: string }> = {
  success:    { label: 'Success',    color: '#10b981' },
  failed:     { label: 'Failed',     color: '#ef4444' },
  blocked:    { label: 'Blocked',    color: '#ef4444' },
  locked:     { label: 'Locked out', color: '#f59e0b' },
};

export const RESULT_ORDER: LoginResult[] = ['success', 'failed', 'blocked', 'locked'];

// Role badge colors — DESIGN_SYSTEM.md §29 (Admin red, Manager blue, User/Viewer gray, Org Admin purple)
export function roleBadgeStyle(role: string | undefined | null): { bg: string; color: string } {
  switch (role) {
    case 'admin': return { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' };
    case 'manager': return { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6' };
    case 'org_admin': return { bg: 'rgba(139,92,246,0.15)', color: '#8b5cf6' };
    default: return { bg: 'rgba(156,163,175,0.15)', color: '#9ca3af' };
  }
}

export function roleLabel(role: string | undefined | null): string {
  switch (role) {
    case 'admin': return 'Admin';
    case 'org_admin': return 'Org Admin';
    case 'manager': return 'Manager';
    case 'viewer': return 'Viewer';
    case 'user': return 'User';
    default: return role ?? '';
  }
}

export const FACILITY_TZ = 'America/New_York';

export function formatTz(dateStr: string | null | undefined, opts: Intl.DateTimeFormatOptions): string {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: FACILITY_TZ, ...opts }).format(new Date(dateStr));
  } catch {
    return '—';
  }
}

export function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const ms = Date.now() - new Date(dateStr).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}

export function dayGroupLabel(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const startOfDay = (x: Date) => new Date(new Intl.DateTimeFormat('en-US', { timeZone: FACILITY_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(x)).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / dayMs);
  const monthDay = formatTz(dateStr, { month: 'short', day: 'numeric' }).toUpperCase();
  if (diffDays === 0) return `TODAY · ${monthDay}`;
  if (diffDays === 1) return `YESTERDAY · ${monthDay}`;
  const weekday = formatTz(dateStr, { weekday: 'short' }).toUpperCase();
  return `${weekday} · ${monthDay}`;
}
